import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FlaskConical, Heart, Sparkles, Check, ShieldCheck, Truck, Award, Headphones, ChevronDown, ArrowRight, Star } from "lucide-react";
import { IMG } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { Reveal, Overline, Btn, SectionHeading } from "@/components/common/ui";

const MARION = "https://static.prod-images.emergentagent.com/jobs/6e9e37ac-1f04-43a6-819a-203cd06632b4/images/d5761b722a4cc152ea481b73d2a9e57a426e5a274718d2dc63238192b29a51cb.png";

const PHILOSOPHY = [
  [FlaskConical, "Science First", "Every wavelength is chosen based on clinical research, never marketing trends."],
  [Heart, "Designed To Be Loved", "Beautiful products people genuinely enjoy reaching for every single day."],
  [Sparkles, "Confidence Starts With Skin", "We believe healthy skin helps people feel more confident in their everyday lives."],
];

const DIFFERENCE = [
  "Clinically-backed LED technology",
  "Comfortable, lightweight design",
  "Professional-quality treatments at home",
  "UV-free and heat-free",
  "Premium, skin-safe materials",
  "A simple 10-minute daily ritual",
];

const PROMISE = [
  [ShieldCheck, "30-Day Money-Back", "Try it risk-free. If it's not for you, return it within 30 days."],
  [Award, "12-Month Warranty", "Every device is fully covered against manufacturing faults."],
  [Truck, "Free Express Shipping", "Tracked, complimentary delivery on every order."],
  [Headphones, "Australian Support", "A real, responsive team here to help whenever you need."],
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#FFFCFA]">
      {/* Hero */}
      <section className="relative flex min-h-[88vh] items-center justify-center overflow-hidden" data-testid="about-hero">
        <img src={M("about_hero")} data-media-slot="about_hero" data-media-type="image" alt="The Aura Glow ritual" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 mx-auto max-w-3xl px-6 text-center text-white">
          <span className="text-[0.7rem] uppercase tracking-[0.32em] text-white/80">About Aura Glow</span>
          <h1 className="mt-6 font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            More than skincare.<br /><span className="italic text-[#FBC4A8]">A daily ritual.</span>
          </h1>
          <p className="mx-auto mt-7 max-w-xl text-base font-light leading-relaxed text-white/90 md:text-lg">
            We're here to make professional LED light therapy beautifully simple, so more people can enjoy healthier,
            brighter skin from the comfort of home.
          </p>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/80" data-testid="about-scroll-indicator">
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </motion.div>
      </section>

      {/* Our Story */}
      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Overline>Our Story</Overline>
          <h2 className="mt-5 font-display text-3xl font-light leading-tight text-[#2C2B2C] sm:text-5xl">
            Skincare had become<br /><span className="italic text-[#E97C6A]">overwhelming.</span>
          </h2>
          <div className="mx-auto mt-8 max-w-xl space-y-6 text-base font-light leading-relaxed text-[#6E6168] md:text-lg">
            <p>Endless products. Expensive treatments. Empty promises. Somewhere along the way, looking after your skin stopped feeling good, and started feeling like a chore.</p>
            <p>We created Aura Glow to change that. By bringing genuinely clinically-backed LED light therapy into one beautifully designed device, simple enough to use every day, we made the most effective part of a routine also the most enjoyable.</p>
            <p>No clinic. No confusion. Just ten quiet minutes and skin that looks, and feels, like you again.</p>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="bg-[#FFF0E8] py-24 md:py-32" data-testid="about-philosophy">
        <div className="mx-auto max-w-[1280px] px-6 md:px-12">
          <SectionHeading align="center" overline="Our Philosophy" title="What we believe in." className="mb-16" />
          <div className="grid gap-6 md:grid-cols-3">
            {PHILOSOPHY.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.1} className="rounded-[2rem] border border-white/60 bg-[#FFFCFA] p-10 text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF0E8] text-[#E97C6A]"><Icon className="h-7 w-7" strokeWidth={1.4} /></span>
                <h3 className="mt-7 font-display text-2xl text-[#2C2B2C]">{t}</h3>
                <p className="mx-auto mt-3 max-w-xs text-sm font-light leading-relaxed text-[#8A7F86]">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our Difference */}
      <section className="py-24 md:py-32" data-testid="about-difference">
        <div className="mx-auto grid max-w-[1280px] items-center gap-14 px-6 md:px-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="overflow-hidden rounded-[2.5rem] border border-[#F2E8E6] shadow-[0_50px_110px_-50px_rgba(120,80,55,0.5)] hover-zoom">
              <img src={M("about_difference")} data-media-slot="about_difference" data-media-type="image" alt="Leopard Luxe LED mask" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Our Difference</Overline>
            <h2 className="mt-4 font-display text-4xl font-light leading-[1.05] tracking-tight text-[#2C2B2C] sm:text-5xl">
              Considered, not<br /><span className="italic text-[#E97C6A]">complicated.</span>
            </h2>
            <ul className="mt-9 space-y-4">
              {DIFFERENCE.map((d) => (
                <li key={d} className="flex items-center gap-4 border-b border-[#F2E8E6] pb-4 text-[#2C2B2C]">
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#FFF0E8] text-[#E97C6A]"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                  <span className="text-base font-light">{d}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Behind The Product */}
      <section className="bg-[#232222] py-24 text-[#FFFCFA] md:py-32 grain" data-testid="about-craft">
        <div className="mx-auto grid max-w-[1280px] items-center gap-14 px-6 md:px-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="order-2 lg:order-1">
            <Overline>Behind The Product</Overline>
            <h2 className="mt-4 font-display text-4xl font-light leading-[1.05] tracking-tight text-[#FFFCFA] sm:text-5xl">
              Crafted to feel<br /><span className="italic text-[#FBC4A8]">effortless.</span>
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#C9BEB8]">
              Soft, flexible silicone that moulds gently to your face. A featherlight, balanced fit you can almost forget you're wearing.
              Considered straps, a beautiful finish and thoughtful details that make ten minutes feel like a moment for yourself.
            </p>
            <p className="mt-4 max-w-md text-base font-light leading-relaxed text-[#C9BEB8]">
              We obsessed over comfort and quality, because a ritual you love is a ritual you'll keep.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-[2.5rem] border border-white/10 hover-zoom">
              <img src={M("about_craft")} data-media-slot="about_craft" data-media-type="image" alt="Close-up craftsmanship of the mask" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Our Promise */}
      <section className="bg-[#FFF0E8] py-24 md:py-32" data-testid="about-promise">
        <div className="mx-auto max-w-[1280px] px-6 md:px-12">
          <SectionHeading align="center" overline="Our Promise" title="You're in good hands." sub="Every order is backed by the kind of care a premium investment in your skin deserves." className="mb-16" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROMISE.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.08} className="rounded-[2rem] border border-white/60 bg-[#FFFCFA] p-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF0E8] text-[#E97C6A]"><Icon className="h-6 w-6" strokeWidth={1.4} /></span>
                <h3 className="mt-5 font-display text-lg text-[#2C2B2C]">{t}</h3>
                <p className="mt-2 text-[0.8rem] font-light leading-relaxed text-[#8A7F86]">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Real Results */}
      <section className="py-24 md:py-32" data-testid="about-results">
        <div className="mx-auto grid max-w-[1280px] items-center gap-14 px-6 md:px-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="mx-auto w-full max-w-[360px]">
            <div className="relative overflow-hidden rounded-[2rem] border border-[#F2E8E6] shadow-[0_40px_90px_-44px_rgba(120,80,55,0.5)]">
              <img src={M("about_results")} data-media-slot="about_results" data-media-type="image" alt="Real customer results over time" className="block w-full" />
              <span className="absolute left-4 top-4 rounded-full bg-[#F2E8E6] px-3 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-[#232222]">Before</span>
              <span className="absolute bottom-4 left-4 rounded-full bg-[#F2E8E6] px-3 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-[#232222]">After · 8 weeks</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Real Results</Overline>
            <h2 className="mt-4 font-display text-4xl font-light leading-[1.05] tracking-tight text-[#2C2B2C] sm:text-5xl">
              Consistency,<br /><span className="italic text-[#E97C6A]">not miracles.</span>
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#6E6168]">
              Our community doesn't chase overnight transformations, they build a habit. The result is calmer, clearer,
              more radiant skin and a quiet confidence that lasts.
            </p>
            <figure className="mt-8 rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] p-6">
              <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-[#E97C6A] text-[#E97C6A]" />)}</div>
              <blockquote className="mt-3 text-[0.92rem] font-light leading-relaxed text-[#3A3438]">
                "I'm 58 and I've tried everything. Eight weeks in, my skin looks genuinely brighter and more even. It's become my favourite part of the day."
              </blockquote>
              <figcaption className="mt-3 text-[0.72rem] uppercase tracking-[0.14em] text-[#8A7F86]">Marion T. · Verified · QLD</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* Closing */}
      <section className="relative overflow-hidden bg-[#232222] py-28 text-center text-[#FFFCFA] md:py-40 grain" data-testid="about-closing">
        <img src={M("about_closing")} data-media-slot="about_closing" data-media-type="image" alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="relative z-10 mx-auto max-w-3xl px-6">
          <h2 className="font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-6xl">
            Your best skin starts with<br /><span className="italic text-[#FBC4A8]">one simple habit.</span>
          </h2>
          <p className="mx-auto mt-7 max-w-lg text-base font-light text-[#C9BEB8] md:text-lg">
            Healthy, glowing skin doesn't come from complicated routines. It comes from consistency, a ritual thousands
            of people genuinely look forward to every day.
          </p>
          <div className="mt-10 flex justify-center">
            <Link to="/shop" data-testid="about-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#FBC4A8] px-9 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-[#232222] transition-transform hover:scale-[1.02]">
              Explore the Collection <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
